import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import LaunchIcon from "@mui/icons-material/Launch";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Menu,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  addBidChoiceEvent,
  fetchLatestBidChoiceEventByReferenceNumberAndEid,
  fetchLatestBidLineEventsByReferenceNumber,
} from "../../api/bid";
import PageLoading from "../../components/PageLoading";
import BidTimer from "../../components/bid/BidTimer";
import ShiftBidChoicesTable from "../../components/bid/ShiftBidChoicesTable";
import ShiftBidReviewTable from "../../components/bid/ShiftBidReviewTable";
import ConfirmButton from "../../components/fundamentals/ConfirmButton";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog";
import InfoDialog from "../../components/fundamentals/InfoDialog";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import StarsCloseIconButton from "../../components/fundamentals/StarsCloseIconButton";
import StarsSelectInput from "../../components/fundamentals/StarsSelectInput";
import SuccessDialog from "../../components/fundamentals/SuccessDialog";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { BID_STATUS, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { filterBidLines, getDisplayedBidLinesWithRadioFilter } from "../../util/bid/filterBidLines";
import { filterAndSortSelectedBidLines } from "../../util/bid/filterBids";
import {
  getEidFromHref,
  getNumberOfMyChoicesLabel,
  getReferenceNumberFromHref,
  getShiftAssignments,
  getShiftDaysOff,
  getShiftPositions,
  getShiftStartTimes,
} from "../../util/bid/shiftBidUtils.js";
import { sortBidDaysOffOptions } from "../../util/bid/sortBids";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils.js";
import { BREADCRUMBS_TITLE, CANCEL_WARNING_MESSAGE_ACTION_TYPE, FEATURES, HOME_PATH } from "../../util/constants.js";
import { getShiftDate } from "../../util/dateTimeFormatter";
import {
  ASSIGNMENT_FILTER_MUST_BE_SELECTED,
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  BID_FORM_GUIDE_MESSAGE,
  DAYS_OFF_FILTER_MUST_BE_SELECTED,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
  POSITION_FILTER_MUST_BE_SELECTED,
  SHIFT_BID_CHOICE_CONFIRMATION_MESSAGE,
  SHIFT_BID_CHOICE_CONFIRMATION_TITLE,
  START_TIME_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import ShiftBidPDFExport from "./ShiftBidPDFExport.jsx";

const useStyles = makeStyles((theme) => ({
  bidDayColumn: {
    whiteSpace: "pre-wrap",
    textAlign: "left",
  },
  filterInputs: {
    width: "100%",
    textAlign: "left",
    backgroundColor: theme.palette.secondary.swaWhite,
  },
  lastSubmitted: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  formControlLabel: {
    fontSize: "12px",
  },
}));

const ShiftBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidLinesLoading, setIsBidLinesLoading] = useState(true);

  // UI State
  const [exportsMenuAnchor, setExportsMenuAnchor] = useState(null);

  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [bidLines, setBidLines] = useState([]); // Original bid lines from the database
  const [filteredBidLines, setFilteredBidLines] = useState([]);
  const [displayedBidLines, setDisplayedBidLines] = useState([]);
  const [selectedBidLines, setSelectedBidLines] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);

  // Filter Input State
  const [filteredPositions, setFilteredPositions] = useState({});
  const [filteredAssignments, setFilteredAssignments] = useState({});
  const [filteredDaysOff, setFilteredDaysOff] = useState({});
  const [filteredStartTimes, setFilteredStartTimes] = useState({});
  const [filterRadioButton, setFilterRadioButton] = useState("showAll");
  const [numOfFilteredMyChoices, setNumOfFilteredMyChoices] = useState(0);

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");
  const [shiftBidFilterDialogOpen, setShiftBidFilterDialogOpen] = useState(false);
  const [modifiedFiltersCount, setModifiedFiltersCount] = useState(0);
  const [isReviewPage, setIsReviewPage] = useState(false);
  const [isEnlargeBidLineDialogOpen, setIsEnlargeBidLineDialogOpen] = useState(false);
  const mobileOriginalAssignmentFiltersRef = useRef([]);
  const mobileOriginalPositionFiltersRef = useRef([]);
  const mobileOriginalDaysOffFiltersRef = useRef([]);
  const mobileOriginalStartTimeFiltersRef = useRef([]);
  const mobileOriginalFilterRadioButtonRef = useRef("");
  const isResetFiltersClicked = useRef(false);
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");

  let history = useHistory();

  useEffect(() => {
    const referenceNumber = getReferenceNumberFromHref();
    const eid = getEidFromHref();

    fetchLatestShiftBidChoices(eid, referenceNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    applyRadioFilters();
  }, [selectedChoices, filterRadioButton, filteredBidLines]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLatestShiftBidChoices = (eid, referenceNumber) => {
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          const userFtPt = response.event.ftpt;
          setBidChoiceData(bidChoiceEvent);
          setSelectedChoices(response.event?.choices ? response.event.choices : []);
          setInitialSelectedChoices(response.event?.choices ? response.event.choices : []);
          setNumOfFilteredMyChoices(response.event?.choices?.length);
          setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
          setLastUpdatedByLastName(response.event?.modifiedByLastName);
          fetchLatestBidLineEventsByReferenceNumber(userSession.eid, referenceNumber)
            .then((res) => res.json())
            .then((response) => {
              if (response?.events) {
                const initFtPtBidLines = response.events
                  .filter((event) => event?.ftpt?.toLowerCase() === userFtPt.toLowerCase() && !event.isDupedLine)
                  .map((event) => {
                    event.id = event.lineNumber;
                    return event;
                  });
                setBidLines(initFtPtBidLines);
                setFilteredBidLines(initFtPtBidLines);
                setDisplayedBidLines(initFtPtBidLines);
                if (props.useAssignmentForEmp) {
                  const shiftAssignments = getShiftAssignments(initFtPtBidLines);
                  setFilteredAssignments(Object.values(shiftAssignments));
                } else {
                  const shiftPositions = getShiftPositions(initFtPtBidLines);
                  setFilteredPositions(Object.values(shiftPositions));
                }
                const shiftDaysOff = getShiftDaysOff(initFtPtBidLines);
                setFilteredDaysOff(Object.values(shiftDaysOff));

                const shiftStartTimes = getShiftStartTimes(initFtPtBidLines);
                setFilteredStartTimes(Object.values(shiftStartTimes));

                if (bidChoiceEvent.status === BID_STATUS.CLOSED || bidChoiceEvent.status === BID_STATUS.AWARDED) {
                  setSelectedBidLines(filterAndSortSelectedBidLines(initFtPtBidLines, bidChoiceEvent.choices));
                  setIsReviewPage(true);
                }

                setIsPageLoading(false);
                setIsBidLinesLoading(false);
              } else {
                throw Error();
              }
            })
            .catch((err) => {
              console.error(`Bid Line Error: %j`, err);
              setBidLines([]);
              setIsBidLinesLoading(false);
            });
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const getModifiedFiltersCount = (
    currFilteredPositions,
    currFilteredAssignments,
    currFilteredDaysOff,
    currFilteredStartTime,
    useAssignmentForEmp
  ) => {
    let count = 0;
    if (useAssignmentForEmp) {
      if (currFilteredAssignments.length !== Object.values(getShiftAssignments(bidLines)).length) {
        count++;
      }
    } else {
      if (currFilteredPositions.length !== Object.values(getShiftPositions(bidLines)).length) {
        count++;
      }
    }

    if (currFilteredDaysOff.length !== Object.values(getShiftDaysOff(bidLines)).length) {
      count++;
    }

    if (currFilteredStartTime.length !== Object.values(getShiftStartTimes(bidLines)).length) {
      count++;
    }

    return count;
  };

  const handleSubmitClicked = () => {
    // TODO: Validation...
    setIsSubmitLoading(true);
    console.log("Saving bid choice data...");
    let payload = buildBidChoiceEvent(prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices }), userSession);
    addBidChoiceEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error("Error: ", result.message);
          setSubmitErrors(result.message);
          setIsSubmitLoading(false);
          setIsSubmitDialogOpen(false);
          setIsSubmitResponseDialogOpen(true);
        } else {
          console.log("Success!");
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
          setIsSubmitResponseDialogOpen(true);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsSubmitLoading(false);
      });
  };

  const handleExportBtnClick = (event) => {
    setExportsMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportsMenuAnchor(null);
  };

  // TODO: remove lint check suppression
  // eslint-disable-next-line
  const handleClickSubmitDialogOpen = () => {
    // TODO: Validation here...
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (!isEqual(selectedChoices, initialSelectedChoices)) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so re-direct to my bids
      history.push(getMyBidsPath());
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleReviewButtonClick = () => {
    setIsReviewPage(true);
    setSelectedBidLines(filterAndSortSelectedBidLines(bidLines, selectedChoices));
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([]);
    setSelectedBidLines([]);
    setNumOfFilteredMyChoices(0);
    setIsClearAllDialogOpen(false);
    setFilterRadioButton("showAll");
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const applyFilters = (newFilteredBidLines) => {
    setFilteredBidLines(newFilteredBidLines);
    setNumOfFilteredMyChoices(getNumberOfMyChoicesLabel(newFilteredBidLines, selectedChoices));
  };

  const applyRadioFilters = () => {
    setNumOfFilteredMyChoices(getNumberOfMyChoicesLabel(filteredBidLines, selectedChoices));

    const newDisplayedBidLines = getDisplayedBidLinesWithRadioFilter(
      filteredBidLines,
      selectedChoices,
      filterRadioButton
    );
    setDisplayedBidLines(newDisplayedBidLines);
  };

  const handlePositionFilterChange = (event, isMobile) => {
    setFilteredPositions(Object.values(event.target.value));

    if (!isMobile) {
      const newFilteredBidLines = filterBidLines(
        bidLines,
        Object.values(event.target.value),
        filteredAssignments,
        filteredDaysOff,
        filteredStartTimes,
        props.useAssignmentForEmp
      );
      applyFilters(newFilteredBidLines);
    }
  };

  const handleAssignmentFilterChange = (event, isMobile) => {
    setFilteredAssignments(Object.values(event.target.value));

    if (!isMobile) {
      const newFilteredBidLines = filterBidLines(
        bidLines,
        filteredPositions,
        Object.values(event.target.value),
        filteredDaysOff,
        filteredStartTimes,
        props.useAssignmentForEmp
      );
      applyFilters(newFilteredBidLines);
    }
  };

  const handleDaysOffFilterChange = (event, isMobile) => {
    setFilteredDaysOff(Object.values(event.target.value));

    if (!isMobile) {
      const newFilteredBidLines = filterBidLines(
        bidLines,
        filteredPositions,
        filteredAssignments,
        Object.values(event.target.value),
        filteredStartTimes,
        props.useAssignmentForEmp
      );
      applyFilters(newFilteredBidLines);
    }
  };

  const handleStartTimesFilterChange = (event, isMobile) => {
    setFilteredStartTimes(Object.values(event.target.value));

    if (!isMobile) {
      const newFilteredBidLines = filterBidLines(
        bidLines,
        filteredPositions,
        filteredAssignments,
        filteredDaysOff,
        Object.values(event.target.value),
        props.useAssignmentForEmp
      );
      applyFilters(newFilteredBidLines);
    }
  };

  const handleAllSelectRadioChange = () => {
    setFilterRadioButton("showAll");
  };

  const handleMyChoicesRadioChange = () => {
    setFilterRadioButton("myChoices");
  };

  const handleUnSelectedRadioChange = () => {
    setFilterRadioButton("unselected");
  };

  const handleResetFiltersClick = (isMobile) => {
    const shiftPositions = getShiftPositions(bidLines);
    setFilteredPositions(Object.values(shiftPositions));

    const shiftAssignments = getShiftAssignments(bidLines);
    setFilteredAssignments(Object.values(shiftAssignments));

    const shiftDaysOff = getShiftDaysOff(bidLines);
    setFilteredDaysOff(Object.values(shiftDaysOff));

    const shiftStartTimes = getShiftStartTimes(bidLines);
    setFilteredStartTimes(Object.values(shiftStartTimes));

    if (isMobile) {
      isResetFiltersClicked.current = true;
    } else {
      const newFilteredBidLines = filterBidLines(
        bidLines,
        Object.values(shiftPositions),
        Object.values(shiftAssignments),
        Object.values(shiftDaysOff),
        Object.values(shiftStartTimes),
        props.useAssignmentForEmp
      );
      applyFilters(newFilteredBidLines);
      setFilterRadioButton("showAll");
    }
  };

  const handleEnlargeBidLinesButtonClicked = (event) => {
    setIsEnlargeBidLineDialogOpen(true);
  };

  const handleEnlargeBidLinesDialogClosed = (event) => {
    setIsEnlargeBidLineDialogOpen(false);
  };

  const getMyBidsPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.MY_BIDS).path;
  };

  const renderEnlargeBidLinesDialog = () => {
    return (
      <Dialog onClose={handleEnlargeBidLinesDialogClosed} open={isEnlargeBidLineDialogOpen} fullScreen>
        {renderBidLines(true)}
      </Dialog>
    );
  };

  const renderPositions = (isMobile) => {
    return (
      <StarsSelectInput
        label="Positions"
        menuMessage="Filter on Positions"
        value={filteredPositions}
        allOptions={Object.values(getShiftPositions(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handlePositionFilterChange(event, isMobile)}
        errors={filteredPositions.length <= 0 && bidLines.length > 0 ? POSITION_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderAssignments = (isMobile) => {
    return (
      <StarsSelectInput
        label="Assignments"
        menuMessage="Filter on Assignments"
        value={filteredAssignments}
        allOptions={Object.values(getShiftAssignments(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handleAssignmentFilterChange(event, isMobile)}
        errors={filteredAssignments.length <= 0 && bidLines.length > 0 ? ASSIGNMENT_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderDaysOff = (isMobile) => {
    return (
      <StarsSelectInput
        label="Days Off"
        menuMessage="Filter on Days Off"
        value={filteredDaysOff}
        allOptions={sortBidDaysOffOptions(Object.values(getShiftDaysOff(bidLines)))}
        fullWidth
        onChange={(event) => handleDaysOffFilterChange(event, isMobile)}
        errors={filteredDaysOff.length <= 0 && bidLines.length > 0 ? DAYS_OFF_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderStartTimes = (isMobile) => {
    return (
      <StarsSelectInput
        label="Start Times"
        menuMessage="Filter on Start Times"
        value={filteredStartTimes}
        allOptions={Object.values(getShiftStartTimes(bidLines))}
        sortComparator={sortOnCaseSensitiveTexts}
        fullWidth
        onChange={(event) => handleStartTimesFilterChange(event, isMobile)}
        errors={filteredStartTimes.length <= 0 && bidLines.length > 0 ? START_TIME_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        multiple={true}
      />
    );
  };

  const renderSubmitShiftBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You can continue to submit choices up until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting choices for <b>{selectedChoices?.length}</b> of <b>{bidLines?.length}</b> bid
                    lines.
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again. You can keep editing
                    your choices until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getBidName = () => {
    return bidChoiceData
      ? `${bidChoiceData?.station} | ${bidChoiceData?.workgroup} | ${getShiftDate(
          bidChoiceData?.schedStartDate
        )} - ${getShiftDate(bidChoiceData?.schedEndDate)}`
      : "";
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const renderDesktopNavBtnsForBidForm = () => {
    return (
      <>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
        </Box>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleExportBtnClick}
            startIcon={<UploadFileIcon />}
            disabled={props.isLoading || !bidLines.length}
          >
            {/* {isValidationLoading ? <CircularProgress size={25} /> : "Submit"} */}
            Export
          </Button>
          <Menu
            className={classes.exportMenu}
            anchorEl={exportsMenuAnchor}
            open={!!exportsMenuAnchor}
            onClose={handleExportMenuClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <ShiftBidPDFExport
              isDesktopView={props.isDesktopView}
              bidLines={bidLines}
              selectedBidLines={filterAndSortSelectedBidLines(bidLines, initialSelectedChoices)}
              eid={userSession.eid}
              bidChoiceData={bidChoiceData}
              lastSubmittedTimestamp={getLastSubmittedTimestamp()}
              onExportMenuClose={handleExportMenuClose}
              isBidLineExportAllowed={
                bidChoiceData.status === BID_STATUS.OPEN || bidChoiceData.status === BID_STATUS.CLOSED
              }
              useAssignmentForEmp={props.useAssignmentForEmp}
            />
          </Menu>
        </Box>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            sx={{
              width: "100px",
              backgroundColor: "secondary.swaGray",
              "&:hover": {
                backgroundColor: "secondary.swaGray5",
              },
            }}
            variant="contained"
            onClick={handleClickGuideDialogOpen}
          >
            Guide
          </Button>
        </Box>
        <Box sx={{ paddingTop: "10px" }}>
          <Button sx={{ width: "100px" }} variant="contained" color="primary" onClick={handleReviewButtonClick}>
            Next
          </Button>
        </Box>
      </>
    );
  };

  const renderDesktopNavBtnsForReview = () => {
    return (
      <>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
        </Box>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            sx={{
              width: "100px",
            }}
            variant="outlined"
            color="primary"
            onClick={() => setIsReviewPage(false)}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ paddingTop: "10px" }}>
          <Button
            sx={{ width: "100px" }}
            variant="contained"
            color="primary"
            onClick={handleClickSubmitDialogOpen}
            disabled={
              bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN || isEqual(selectedChoices, initialSelectedChoices)
            }
          >
            Submit
          </Button>
        </Box>
      </>
    );
  };

  const renderExportAndCloseNavBtnsForDesktop = () => {
    return (
      <>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            onClick={handleExportBtnClick}
            startIcon={<UploadFileIcon />}
            disabled={props.isLoading || !bidLines.length}
          >
            Export
          </Button>
          <Menu
            className={classes.exportMenu}
            anchorEl={exportsMenuAnchor}
            open={!!exportsMenuAnchor}
            onClose={handleExportMenuClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <ShiftBidPDFExport
              isDesktopView={props.isDesktopView}
              bidLines={bidLines}
              selectedBidLines={filterAndSortSelectedBidLines(bidLines, bidChoiceData.choices)}
              eid={userSession.eid}
              bidChoiceData={bidChoiceData}
              lastSubmittedTimestamp={getLastSubmittedTimestamp()}
              onExportMenuClose={handleExportMenuClose}
              isBidLineExportAllowed={
                bidChoiceData.status === BID_STATUS.OPEN || bidChoiceData.status === BID_STATUS.CLOSED
              }
              useAssignmentForEmp={props.useAssignmentForEmp}
            />
          </Menu>
        </Box>
        <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
          <Button
            sx={{
              width: "100px",
            }}
            variant="contained"
            color="primary"
            onClick={() =>
              history.push({
                pathname: getMyBidsPath(),
              })
            }
          >
            Close
          </Button>
        </Box>
      </>
    );
  };

  const renderDesktopNavBtns = () => {
    if (isReviewPage) {
      if (bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED) {
        return renderExportAndCloseNavBtnsForDesktop();
      } else {
        return renderDesktopNavBtnsForReview();
      }
    } else {
      return renderDesktopNavBtnsForBidForm();
    }
  };

  const renderBidLines = (isDialog) => {
    return (
      <>
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaLightestBlue",
            paddingLeft: "1rem",
            paddingRight: "1rem",
          }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{ alignItems: "center", justifyContent: "space-between", paddingTop: "6px" }}
          >
            <Typography sx={{ fontWeight: "bold", color: "primary.swaBlue" }}>
              Showing {filteredBidLines.length} of {bidLines.length} {bidChoiceData.ftpt} bid lines
            </Typography>
            {isDialog ? (
              <Grid item xs={1} sx={{ textAlign: "right", paddingRight: "1rem" }}>
                <StarsCloseIconButton onClick={handleEnlargeBidLinesDialogClosed} />
              </Grid>
            ) : (
              <IconButton sx={{ padding: 0 }} onClick={handleEnlargeBidLinesButtonClicked}>
                <LaunchIcon color="primary" />
              </IconButton>
            )}
          </Grid>

          <Grid container item sx={{ alignItems: "center", direction: "row", justifyContent: "space-between" }}>
            <Grid container item xs={10} sx={{ padding: "1rem 1rem 1rem 0" }}>
              <Grid item xs={2}>
                {props.useAssignmentForEmp ? renderAssignments(false) : renderPositions(false)}
              </Grid>
              <Grid item xs={2} sx={{ marginLeft: "1rem" }}>
                {renderDaysOff(false)}
              </Grid>
              <Grid item xs={2} sx={{ marginLeft: "1rem" }}>
                {renderStartTimes(false)}
              </Grid>
            </Grid>
            <Grid item>
              <Button
                sx={{ fontWeight: "bold", textDecoration: "underline", padding: 0 }}
                onClick={() => handleResetFiltersClick(false)}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray1",
            paddingLeft: "1rem",
          }}
        >
          <Stack direction="row">
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "start" }}>
              <Typography sx={{ fontWeight: "bold", marginTop: "8px" }}>Bid Line Filter:</Typography>
              <Box sx={{ paddingLeft: "1rem" }}>
                <FormControl>
                  <RadioGroup row>
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label={`All (${filteredBidLines.length})`}
                      checked={filterRadioButton === "showAll"}
                      onChange={handleAllSelectRadioChange}
                    />
                    <FormControlLabel
                      value="myChoices"
                      control={<Radio />}
                      label={`My Choices (${numOfFilteredMyChoices})`}
                      checked={filterRadioButton === "myChoices"}
                      onChange={handleMyChoicesRadioChange}
                    />
                    <FormControlLabel
                      value="unselected"
                      control={<Radio />}
                      label={`Unselected (${filteredBidLines.length - numOfFilteredMyChoices})`}
                      checked={filterRadioButton === "unselected"}
                      onChange={handleUnSelectedRadioChange}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Grid>
          </Stack>
        </Grid>

        {/* Choices Entered: */}
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray2",
            paddingLeft: "1rem",
          }}
        >
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedChoices.length} {`Choice${selectedChoices.length > 1 ? "s" : ""}`} Entered
            </Typography>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
            <Button sx={{ fontWeight: "bold", textDecoration: "underline" }} onClick={handleClickClearAllDialogOpen}>
              Clear All
            </Button>
            <ConfirmationDialog
              isDialogOpen={isClearAllDialogOpen}
              title={BID_FORM_CLEAR_ALL}
              message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
              handleNoButtonClicked={handleClickCancelClearAllDialogClose}
              handleYesButtonClicked={handleClearAllChoices}
              noTitle="Cancel"
              yesTitle="Clear All"
            />
          </Grid>
        </Grid>

        {/* Choice / Bid Line */}
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
          }}
        >
          <Box
            sx={{
              height: isDialog ? `calc(85vh - 100px)` : `calc(85vh - 340px)`,
              width: "inherit",
              "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
              "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
              "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
            }}
          >
            <ShiftBidChoicesTable
              filteredBidLines={displayedBidLines}
              isBidLinesLoading={isBidLinesLoading}
              selectedChoices={selectedChoices}
              setSelectedChoices={setSelectedChoices}
              isDesktopView={true}
              useAssignmentForEmp={props.useAssignmentForEmp}
            />
          </Box>
        </Grid>
      </>
    );
  };

  const renderMobileNavBtnsForBidForm = () => {
    return (
      <>
        <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
          <Button
            sx={{
              width: "100px",
              backgroundColor: "secondary.swaGray",
              "&:hover": {
                backgroundColor: "secondary.swaGray5",
              },
            }}
            variant="contained"
            onClick={handleClickGuideDialogOpen}
          >
            Guide
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
          <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
          <Button
            sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
            variant="contained"
            color="primary"
            onClick={handleReviewButtonClick}
          >
            Next
          </Button>
        </Grid>
      </>
    );
  };

  const renderMobileNavBtnsForReview = () => {
    return (
      <>
        <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
          <Button
            sx={{
              width: "100px",
            }}
            variant="outlined"
            color="primary"
            onClick={() => {
              setIsReviewPage(false);
            }}
          >
            Back
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
          <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
            Cancel
          </Button>
          <Button
            sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
            variant="contained"
            color="primary"
            onClick={handleClickSubmitDialogOpen}
            disabled={
              bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN || isEqual(selectedChoices, initialSelectedChoices)
            }
          >
            Submit
          </Button>
        </Grid>
      </>
    );
  };

  const renderExportAndCloseNavBtnsForMobile = () => {
    return (
      <Grid container justifyContent="space-between" direction="row" alignItems="center" sx={{ margin: "5px" }}>
        <Grid item xs={4} sx={{ padding: 1, display: "flex", justifyContent: "start" }}>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={handleExportBtnClick}
              startIcon={<UploadFileIcon />}
              disabled={props.isLoading || !bidLines.length}
            >
              Export
            </Button>
            <Menu
              className={classes.exportMenu}
              anchorEl={exportsMenuAnchor}
              open={!!exportsMenuAnchor}
              onClose={handleExportMenuClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ShiftBidPDFExport
                isDesktopView={props.isDesktopView}
                bidLines={bidLines}
                selectedBidLines={filterAndSortSelectedBidLines(bidLines, bidChoiceData.choices)}
                eid={userSession.eid}
                bidChoiceData={bidChoiceData}
                lastSubmittedTimestamp={getLastSubmittedTimestamp()}
                onExportMenuClose={handleExportMenuClose}
                isBidLineExportAllowed={
                  bidChoiceData.status === BID_STATUS.OPEN || bidChoiceData.status === BID_STATUS.CLOSED
                }
                useAssignmentForEmp={props.useAssignmentForEmp}
              />
            </Menu>
          </Box>
        </Grid>
        <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
          <Box>
            <Button
              sx={{
                width: "110px",
              }}
              variant="contained"
              color="primary"
              onClick={() =>
                history.push({
                  pathname: getMyBidsPath(),
                })
              }
            >
              Close
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderMobileNavBtns = () => {
    if (isReviewPage) {
      if (bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED) {
        return renderExportAndCloseNavBtnsForMobile();
      } else {
        return renderMobileNavBtnsForReview();
      }
    } else {
      return renderMobileNavBtnsForBidForm();
    }
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_BIDS, getBidName()];
  const breadcrumbsLinks = [HOME_PATH, `/${FEATURES.MY_BIDS}`];
  const renderShiftBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {isEnlargeBidLineDialogOpen && renderEnlargeBidLinesDialog()}
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={!isEqual(selectedChoices, initialSelectedChoices)}
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
        />

        {/* Title & Cancel/Submit Buttons */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
              <Grid item>
                <Typography variant="h5" component="div" align="left">
                  {getBidName()} Shift Bid
                </Typography>

                {getLastSubmittedTimestamp() !== "" &&
                lastUpdatedByFirstName &&
                lastUpdatedByLastName &&
                lastUpdatedByFirstName !== "" &&
                lastUpdatedByLastName !== "" ? (
                  <Grid className={classes.lastSubmitted}>
                    {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                  </Grid>
                ) : getLastSubmittedTimestamp() !== "" ? (
                  <Grid
                    className={classes.lastSubmitted}
                  >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
                ) : (
                  ""
                )}

                <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                  {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                    <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                      <BidTimer
                        windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                        station={bidChoiceData.station}
                      />
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Stack>
              <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                {renderDesktopNavBtns()}
              </Grid>
            </Stack>
          </Grid>
          <Box
            sx={{
              width: "100%",
              margin: "auto",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              marginTop: "1rem",
            }}
          >
            {isReviewPage ? (
              <>
                <Grid
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid sx={{ height: "40px", alignContent: "center" }}>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      Review your{" "}
                      {bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                        ? bidChoiceData.choices
                          ? bidChoiceData.choices.length
                          : 0
                        : selectedChoices?.length}{" "}
                      choices
                    </Typography>
                  </Grid>
                  {bidChoiceData.status !== BID_STATUS.CLOSED && bidChoiceData.status !== BID_STATUS.AWARDED && (
                    <Grid sx={{ marginLeft: "5px", marginBottom: "5px" }}>
                      <li> You can use the up/down arrows to reorder your choices</li>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 200px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <ShiftBidReviewTable
                      selectedBidLines={selectedBidLines}
                      setSelectedChoices={setSelectedChoices}
                      setSelectedBidLines={setSelectedBidLines}
                      selectedChoices={
                        bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                          ? bidChoiceData.choices
                          : selectedChoices
                      }
                      isBidLinesLoading={isBidLinesLoading}
                      isDesktopView={true}
                      isReadOnly={
                        bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                      }
                      useAssignmentForEmp={props.useAssignmentForEmp}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>{renderBidLines(false)}</>
            )}
          </Box>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const handleShiftBidFiltersDialogOpen = () => {
    mobileOriginalPositionFiltersRef.current = filteredPositions;
    mobileOriginalAssignmentFiltersRef.current = filteredAssignments;
    mobileOriginalDaysOffFiltersRef.current = filteredDaysOff;
    mobileOriginalStartTimeFiltersRef.current = filteredStartTimes;
    mobileOriginalFilterRadioButtonRef.current = filterRadioButton;
    setShiftBidFilterDialogOpen(true);
  };

  const revertMobileFilters = () => {
    setFilteredPositions(mobileOriginalPositionFiltersRef.current);
    setFilteredAssignments(mobileOriginalAssignmentFiltersRef.current);
    setFilteredDaysOff(mobileOriginalDaysOffFiltersRef.current);
    setFilteredStartTimes(mobileOriginalStartTimeFiltersRef.current);
    setFilterRadioButton(mobileOriginalFilterRadioButtonRef.current);

    setModifiedFiltersCount(
      getModifiedFiltersCount(
        mobileOriginalPositionFiltersRef.current,
        mobileOriginalAssignmentFiltersRef.current,
        mobileOriginalDaysOffFiltersRef.current,
        mobileOriginalStartTimeFiltersRef.current,
        props.useAssignmentForEmp
      )
    );

    let newFilteredBidLines = filterBidLines(
      bidLines,
      mobileOriginalPositionFiltersRef.current,
      mobileOriginalAssignmentFiltersRef.current,
      mobileOriginalDaysOffFiltersRef.current,
      mobileOriginalStartTimeFiltersRef.current,
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);
  };

  const handleShiftBidFilterDialogClose = () => {
    revertMobileFilters();
    setShiftBidFilterDialogOpen(false);
  };

  const handleApplyFiltersClick = () => {
    let newFilteredBidLines = filterBidLines(
      bidLines,
      filteredPositions,
      filteredAssignments,
      filteredDaysOff,
      filteredStartTimes,
      props.useAssignmentForEmp
    );
    applyFilters(newFilteredBidLines);

    if (isResetFiltersClicked.current) {
      setFilterRadioButton("showAll");
      isResetFiltersClicked.current = false;
    }

    setModifiedFiltersCount(
      getModifiedFiltersCount(
        filteredPositions,
        filteredAssignments,
        filteredDaysOff,
        filteredStartTimes,
        props.useAssignmentForEmp
      )
    );

    setShiftBidFilterDialogOpen(false);
  };

  const renderShiftBidFilters = () => {
    return (
      <Dialog
        fullScreen
        className="detail-dialog"
        fullWidth
        open={shiftBidFilterDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={handleShiftBidFilterDialogClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        data-testid="trade-filter-form-mobile"
      >
        <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
          Filters
        </DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.useAssignmentForEmp ? renderAssignments(true) : renderPositions(true)}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderDaysOff(true)}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderStartTimes(true)}
                </Box>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <Grid container justifyContent="center" sx={{ p: 4 }}>
          <Link component="button" variant="body2" onClick={() => handleResetFiltersClick(true)}>
            <b>RESET FILTERS</b>
          </Link>
        </Grid>
        <DialogActions>
          <Button
            className="dialog-left-button"
            variant="outlined"
            color="primary"
            onClick={handleShiftBidFilterDialogClose}
          >
            Cancel
          </Button>
          <Button
            className="dialog-right-button-2"
            variant="contained"
            color="primary"
            onClick={handleApplyFiltersClick}
            disabled={
              props.useAssignmentForEmp
                ? filteredAssignments.length <= 0 || filteredDaysOff.length <= 0 || filteredStartTimes.length <= 0
                : filteredPositions.length <= 0 || filteredDaysOff.length <= 0 || filteredStartTimes.length <= 0
            }
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderMobileShiftBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {/* Title */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "1rem", paddingTop: "20px" }}>
              <Typography variant="h5" component="div" align="left" fontSize={"20px"}>
                {bidChoiceData && `${bidChoiceData?.station} | ${bidChoiceData?.workgroup}`} <br />
                {bidChoiceData &&
                  `${getShiftDate(bidChoiceData?.schedStartDate)} - ${getShiftDate(bidChoiceData?.schedEndDate)} ${
                    bidChoiceData.bidType
                  }`}
              </Typography>

              {getLastSubmittedTimestamp() !== "" &&
              lastUpdatedByFirstName &&
              lastUpdatedByLastName &&
              lastUpdatedByFirstName !== "" &&
              lastUpdatedByLastName !== "" ? (
                <Grid className={classes.lastSubmitted}>
                  {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                </Grid>
              ) : getLastSubmittedTimestamp() !== "" ? (
                <Grid
                  className={classes.lastSubmitted}
                >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
              ) : (
                ""
              )}

              <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                  <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "12px" }}>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <AccessTimeIcon fontSize="12px" />
                      </Grid>
                      <Grid item xs={11.2} sm={11.2} style={{ fontWeight: "bold", fontSize: "10px" }}>
                        <BidTimer
                          windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                          station={bidChoiceData.station}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              width: "100%",
              margin: "auto",
              marginTop: "10px",
            }}
          >
            {isReviewPage ? (
              <>
                <Grid
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      Review your{" "}
                      {bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                        ? bidChoiceData.choices
                          ? bidChoiceData.choices.length
                          : 0
                        : selectedChoices?.length}{" "}
                      choices
                    </Typography>
                  </Grid>
                  {bidChoiceData.status !== BID_STATUS.CLOSED && bidChoiceData.status !== BID_STATUS.AWARDED && (
                    <Grid sx={{ marginLeft: "5px" }}>
                      <li>You can use the up/down arrows to reorder your choices</li>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 160px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <ShiftBidReviewTable
                      selectedBidLines={selectedBidLines}
                      setSelectedChoices={setSelectedChoices}
                      setSelectedBidLines={setSelectedBidLines}
                      selectedChoices={
                        bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                          ? bidChoiceData.choices
                          : selectedChoices
                      }
                      isBidLinesLoading={isBidLinesLoading}
                      isDesktopView={false}
                      isReadOnly={
                        bidChoiceData.status === BID_STATUS.CLOSED || bidChoiceData.status === BID_STATUS.AWARDED
                      }
                      useAssignmentForEmp={props.useAssignmentForEmp}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                {/* Filter Bid Lines */}
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                >
                  <Grid item xs={7.5} sx={{ textAlign: "left", paddingTop: "16px" }}>
                    <Box height={"50px"}>
                      <Typography sx={{ fontWeight: "bold", color: "primary.swaBlue" }}>
                        Showing {filteredBidLines.length} of {bidLines.length} {bidChoiceData.ftpt} bid lines
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4.5}>
                    <Button
                      style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                      size="small"
                      fullWidth
                      onClick={() => {
                        handleShiftBidFiltersDialogOpen();
                      }}
                      sx={{ border: "solid 0.5px", borderColor: "secondary.swaBlue" }}
                    >
                      <Grid
                        container
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="center"
                        alignContent="center"
                      >
                        <Grid item alignItems="center" xs={3} sm={2} md={1}>
                          <FilterAltIcon sx={{ marginTop: 1, fontSize: "18px" }} />
                        </Grid>
                        <Grid item xs={9} sm={10} md={11}>
                          <Typography sx={{ fontSize: "14px" }}>
                            {modifiedFiltersCount > 0
                              ? `${modifiedFiltersCount} Filter${modifiedFiltersCount > 1 ? "s" : ""}`
                              : "Filters"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Button>
                  </Grid>
                </Grid>
                <Divider />
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "10px",
                  }}
                >
                  <Stack direction="row">
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "start" }}>
                      <Box>
                        <FormControl>
                          <RadioGroup row>
                            <FormControlLabel
                              value="all"
                              control={<Radio />}
                              label={
                                <Typography
                                  className={classes.formControlLabel}
                                >{`All (${filteredBidLines.length})`}</Typography>
                              }
                              checked={filterRadioButton === "showAll"}
                              onChange={handleAllSelectRadioChange}
                            />
                            <FormControlLabel
                              value="myChoices"
                              control={<Radio />}
                              label={
                                <Typography
                                  className={classes.formControlLabel}
                                >{`My Choices (${numOfFilteredMyChoices})`}</Typography>
                              }
                              checked={filterRadioButton === "myChoices"}
                              onChange={handleMyChoicesRadioChange}
                            />
                            <FormControlLabel
                              value="unselected"
                              control={<Radio />}
                              label={
                                <Typography className={classes.formControlLabel}>{`Unselected (${
                                  filteredBidLines.length - numOfFilteredMyChoices
                                })`}</Typography>
                              }
                              checked={filterRadioButton === "unselected"}
                              onChange={handleUnSelectedRadioChange}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    </Grid>
                  </Stack>
                </Grid>

                {/* Choices Entered: */}
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {selectedChoices.length} {`Choice${selectedChoices.length > 1 ? "s" : ""}`} Entered
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>

                {/* Choices / Bid Line */}
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 100px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <ShiftBidChoicesTable
                      filteredBidLines={displayedBidLines}
                      isBidLinesLoading={isBidLinesLoading}
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      useAssignmentForEmp={props.useAssignmentForEmp}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </Box>

          <Grid
            container
            sx={{
              marginTop: "5px",
              bottom: 0,
              position: "fixed",
              backgroundColor: "secondary.swaWhite",
              zIndex: 999,
            }}
          >
            {renderMobileNavBtns()}
          </Grid>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderDialogs = () => {
    return (
      <>
        <ConfirmationDialog
          isDialogOpen={isExitDialogOpen}
          title="Cancel"
          message={BID_FORM_CANCEL_MESSAGE}
          handleNoButtonClicked={handleClickCancelDialogClose}
          redirectTo={getMyBidsPath()}
        />
        {renderSubmitShiftBidChoiceDialog()}
        {shiftBidFilterDialogOpen && renderShiftBidFilters()}
        <InfoDialog
          isDialogOpen={isGuideDialogOpen}
          title="Guide"
          message={BID_FORM_GUIDE_MESSAGE()}
          handleCloseClicked={handleCloseClickedGuideDialog}
          isDesktopView={props.isDesktopView}
          bidLines={bidLines}
          selectedBidLines={filterAndSortSelectedBidLines(bidLines, initialSelectedChoices)}
          eid={userSession.eid}
          bidChoiceData={bidChoiceData}
          lastSubmittedTimestamp={getLastSubmittedTimestamp()}
          useAssignmentForEmp={props.useAssignmentForEmp}
        />
        <SuccessDialog
          isSuccessDialogOpen={isSubmitResponseDialogOpen}
          title={SHIFT_BID_CHOICE_CONFIRMATION_TITLE}
          message={SHIFT_BID_CHOICE_CONFIRMATION_MESSAGE}
          hasError={submitErrors !== ""}
          error={BID_CHOICE_SAVE_ERROR_MESSAGE}
          redirectTo={submitErrors === "" && getMyBidsPath()}
          handleOKClick={() => setIsSubmitResponseDialogOpen(false)}
        />
      </>
    );
  };

  return (
    <div className="App-contents enable-scroll" data-testid="shift-bid-choices-page">
      {props.isDesktopView ? renderShiftBidChoicesPage() : renderMobileShiftBidChoicesPage()}
    </div>
  );
};

ShiftBidChoices.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  featureConfig: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  useAssignmentForEmp: PropTypes.bool.isRequired,
};

export default ShiftBidChoices;
